import { BaseGenerator } from './BaseGenerator.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Mini-mall (strip mall) generator.
 * Produces a strip mall with typed stores, each with front retail + back storage,
 * shared parking lot, and loading area.
 *
 * Options:
 * - stores: 3-5 (default 3)
 * - quality: 'draft' | 'standard' | 'high'
 *
 * Ported from OMN's generateMiniMall.
 */
export class MiniMallGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';
    const tiles = content.tiles || MiniMallGenerator.DEFAULT_TILES;
    const opts = { ...MiniMallGenerator.DEFAULT_OPTIONS, ...this.options };
    const stores = Math.max(3, Math.min(5, opts.stores || 3));

    const storeW = this.rng.nextInt(7, 9);
    const mallH = this.rng.nextInt(10, 14);
    const mallW = storeW * stores + 2;

    const hl = Math.floor((this.width - mallW) / 2);
    const ht = Math.floor(this.height / 2) - mallH;
    const hr = hl + mallW;
    const hb = ht + mallH;

    const roadTop = hb + 8;
    const roadHeight = 3;
    const sidewalkN = roadTop - 1;
    const sidewalkS = roadTop + roadHeight;
    const parkingTop = hb + 1;
    const parkingBot = roadTop;

    const ground = this.createGrid(this.width, this.height, ZONE.GRASS);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const rooms = [];

    // 1. Grass
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        ground[y][x] = ZONE.GRASS;
      }
    }
    this._addGrassVariation(ground, 5);

    // 2. Parking lot
    this.fillRect(ground, hl - 3, parkingTop, hr + 2, parkingBot - 1, ZONE.ASPHALT);

    // 3. Road + sidewalks
    this._fillRoad(ground, roadTop, roadHeight);
    this._fillSidewalkRow(ground, sidewalkN);
    this._fillSidewalkRow(ground, sidewalkS);

    // 4. Building interior
    this.fillRect(ground, hl, ht, hr - 1, hb - 1, ZONE.FLOOR);

    // 5. Perimeter walls
    this._placePerimeterWalls(objects, hl, ht, hr, hb);

    // 6. Assign store types
    const allStoreTypes = ['pizza', 'laundromat', 'nail_salon', 'liquor_store', 'dollar_store'];
    const shuffled = this.rng.shuffle([...allStoreTypes]);
    const assignedTypes = [];
    for (let i = 0; i < stores; i++) {
      assignedTypes.push(shuffled[i % shuffled.length]);
    }

    // Store type to zone mapping
    const storeTypeZones = {
      pizza: ZONE.KITCHEN,
      laundromat: ZONE.LAUNDRY,
      nail_salon: ZONE.STORE,
      liquor_store: ZONE.STORE,
      dollar_store: ZONE.STORE,
    };

    // 7. Divide into store units
    for (let si = 0; si < stores; si++) {
      const sx = hl + 1 + si * storeW;
      const sy = ht + 1;
      const sw = Math.min(storeW, hr - 1 - sx);
      const sh = mallH - 2;

      if (sw < 3) continue;

      const storeType = assignedTypes[si];
      const storeZone = storeTypeZones[storeType] || ZONE.STORE;

      // Fill store floor
      this.fillRect(ground, sx, sy, sx + sw - 1, sy + sh - 1, storeZone);

      // Split: front (70%) + back (30%)
      const backRoomH = Math.max(3, Math.floor(sh * 0.3));
      const frontH = sh - backRoomH;
      const backWallRow = sy + frontH;

      // Interior horizontal wall
      for (let c = sx; c < sx + sw; c++) {
        this.setCell(objects, c, backWallRow, ZONE.WALL_INT);
      }
      // Door in back wall
      const backDoorCol = Math.floor(sx + sw / 2);
      this.setCell(objects, backDoorCol, backWallRow, ZONE.DOORFRAME);
      if (backDoorCol + 1 < sx + sw) {
        this.setCell(objects, backDoorCol + 1, backWallRow, ZONE.DOORFRAME);
      }

      // Back room floor (storage)
      this.fillRect(ground, sx, backWallRow + 1, sx + sw - 1, sy + sh - 1, ZONE.STORAGE);

      // Vertical wall between stores
      if (si > 0) {
        for (let r = ht + 1; r < hb - 1; r++) {
          this.setCell(objects, sx, r, ZONE.WALL_INT);
        }
      }

      // Store front door (south wall)
      const doorCol = Math.floor(sx + sw / 2);
      if (doorCol > hl && doorCol < hr - 1) {
        this.setCell(objects, doorCol, hb - 1, ZONE.DOOR);
        this.setCell(ground, doorCol, hb - 1, storeZone);
      }

      // Store windows (south wall, flanking door)
      for (let c = sx + 1; c < sx + sw - 1; c += 3) {
        if (c !== doorCol && c >= hl + 1 && c < hr - 1) {
          if (this.getCell(objects, c, hb - 1) === ZONE.WALL_EXT) {
            this.setCell(objects, c, hb - 1, ZONE.WINDOW);
          }
        }
      }

      // Front room
      rooms.push({ x: sx, y: sy, width: sw, height: frontH, type: storeType });
      // Back room
      rooms.push({ x: sx, y: backWallRow + 1, width: sw, height: backRoomH - 1, type: 'storage' });

      // Store-specific blocking props in corners
      this.setCell(objects, sx + 1, sy + 1, ZONE.BARREL);
      if (sw > 4) {
        this.setCell(objects, sx + sw - 2, sy + 1, ZONE.CRATE);
      }
      // Back room blocking
      this.setCell(objects, sx + 1, backWallRow + 2 < sy + sh ? backWallRow + 2 : backWallRow + 1, ZONE.CRATE);
    }

    // 8. Loading area behind building (north side)
    for (let i = 0; i < this.rng.nextInt(2, 4); i++) {
      this.setCell(objects, hl + this.rng.nextInt(2, mallW - 3), ht - this.rng.nextInt(1, 3), ZONE.CRATE);
    }
    this.setCell(objects, hr + 1, ht + 1, ZONE.BARREL);
    this.setCell(objects, hr + 1, ht + 3, ZONE.BARREL);

    // 9. Parking lot cars
    const carCount = this.rng.nextInt(3, 5);
    for (let i = 0; i < carCount; i++) {
      const col = hl + this.rng.nextInt(0, mallW);
      this.setCell(objects, col, parkingTop + this.rng.nextInt(1, parkingBot - parkingTop - 2), ZONE.CRATE);
    }

    // 10. Room decorations
    this._decorateRooms(objects, rooms);

    // 11. Trees + road cars
    this._scatterYardDecorations(objects, hl - 3, ht - 3, hr + 3, roadTop,
      this.rng.nextInt(3, 5), this.rng.nextInt(2, 4));
    this._placeRoadCars(objects, roadTop, hl - 3, hr + 3, this.rng.nextInt(1, 3));

    if (quality !== 'draft') {
      const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });
      if (quality !== 'high') pipeline.removePass('decoration');

      const pipelineOptions = {
        tileMapping: this._buildTileMapping(tiles),
        floorTileIds: [ZONE.FLOOR, ZONE.DOOR, ZONE.STORE, ZONE.STORAGE,
          ZONE.KITCHEN, ZONE.LAUNDRY],
        corridorTileId: ZONE.FLOOR,
        cleanup: { minPatchSize: 0, smoothIterations: 0 },
      };

      const result = pipeline.run({
        ground, objects, overhead,
        zones: ground,
        width: this.width, height: this.height,
        metadata: { rooms },
      }, pipelineOptions);

      const finalGround = this._resolveZones(result.ground, tiles);
      const finalObjects = this._resolveObjectZones(result.objects, tiles);
      const finalOverhead = result.overhead.map(row => [...row]);

      if (tiles._tileLayers) {
        this.redistributeByLayer(
          { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
          tiles._tileLayers,
        );
      }

      return this.buildTMJ({
        layers: [
          { name: 'Ground', data: this.flattenGrid(finalGround) },
          { name: 'Objects', data: this.flattenGrid(finalObjects) },
          { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
        ],
        tilesets: content.tilesets || [],
        properties: {
          generator: { type: 'string', value: 'mini_mall' },
          rooms: { type: 'string', value: JSON.stringify(rooms) },
        },
      });
    }

    const draftGround = this._resolveZones(ground, tiles);
    const draftObjects = this._resolveObjectZones(objects, tiles);
    const draftOverhead = overhead.map(row => [...row]);

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: draftGround, objects: draftObjects, overhead: draftOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(draftGround) },
        { name: 'Objects', data: this.flattenGrid(draftObjects) },
        { name: 'Overhead', data: this.flattenGrid(draftOverhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        generator: { type: 'string', value: 'mini_mall' },
        rooms: { type: 'string', value: JSON.stringify(rooms) },
      },
    });
  }

  // ---- Helpers ----

  _decorateRooms(objects, rooms) {
    for (const room of rooms) {
      if (!room.type || room.width < 3 || room.height < 3) continue;
      // Place counter along back wall (top of room)
      for (let x = room.x + 1; x < room.x + room.width - 1; x++) {
        if (this.rng.chance(0.6)) {
          this.setCell(objects, x, room.y + 1, ZONE.COUNTER);
        }
      }
      // Scatter a few crates/barrels
      const itemCount = this.rng.nextInt(1, 3);
      for (let i = 0; i < itemCount; i++) {
        const x = this.rng.nextInt(room.x + 1, room.x + room.width - 2);
        const y = this.rng.nextInt(room.y + 2, room.y + room.height - 2);
        if (this.getCell(objects, x, y) === 0 || this.getCell(objects, x, y) === ZONE.EMPTY) {
          this.setCell(objects, x, y, this.rng.pick([ZONE.CRATE, ZONE.BARREL, ZONE.TABLE]));
        }
      }
    }
  }

  _fillRoad(ground, roadTop, roadHeight) {
    for (let r = roadTop; r < roadTop + roadHeight && r < this.height; r++) {
      for (let c = 0; c < this.width; c++) {
        this.setCell(ground, c, r, ZONE.ROAD);
      }
    }
  }

  _fillSidewalkRow(ground, row) {
    if (row < 0 || row >= this.height) return;
    for (let c = 0; c < this.width; c++) {
      this.setCell(ground, c, row, ZONE.SIDEWALK);
    }
  }

  _placePerimeterWalls(objects, hl, ht, hr, hb) {
    for (let c = hl; c < hr; c++) {
      this.setCell(objects, c, ht, ZONE.WALL_EXT);
      this.setCell(objects, c, hb - 1, ZONE.WALL_EXT);
    }
    for (let r = ht; r < hb; r++) {
      this.setCell(objects, hl, r, ZONE.WALL_EXT);
      this.setCell(objects, hr - 1, r, ZONE.WALL_EXT);
    }
  }

  _scatterYardDecorations(objects, hl, ht, hr, hb, treeCount, bushCount) {
    const margin = 3;
    const placed = new Set();
    const tryPlace = (zone, count) => {
      let n = 0;
      for (let attempt = 0; attempt < count * 10 && n < count; attempt++) {
        const c = this.rng.nextInt(margin, this.width - margin - 1);
        const r = this.rng.nextInt(margin, this.height - margin - 1);
        if (c >= hl - 1 && c < hr + 1 && r >= ht - 1 && r < hb + 1) continue;
        const key = `${c},${r}`;
        if (placed.has(key)) continue;
        placed.add(key);
        this.setCell(objects, c, r, zone);
        n++;
      }
    };
    tryPlace(ZONE.TREE, treeCount);
    tryPlace(ZONE.BUSH, bushCount);
  }

  _placeRoadCars(objects, roadTop, hl, hr, count) {
    for (let i = 0; i < count; i++) {
      const c = this.rng.nextInt(4, this.width - 4);
      if (c >= hl - 1 && c <= hr + 1) continue;
      this.setCell(objects, c, roadTop + 1, ZONE.CRATE);
    }
  }

  _buildTileMapping(tiles) {
    const mapping = {};
    mapping[ZONE.GRASS] = { center: tiles.grass || 1 };
    mapping[ZONE.ROAD] = { center: tiles.road || tiles.asphalt || 1 };
    mapping[ZONE.SIDEWALK] = { center: tiles.sidewalk || 1 };
    mapping[ZONE.ASPHALT] = { center: tiles.asphalt || 1 };
    mapping[ZONE.WALL_EXT] = { center: tiles.wallExt || tiles.wall };
    mapping[ZONE.WALL_INT] = { center: tiles.wallInt || tiles.wall };
    mapping[ZONE.FLOOR] = { center: tiles.floor };
    mapping[ZONE.STORE] = { center: tiles.floorStore || tiles.floor };
    mapping[ZONE.STORAGE] = { center: tiles.floorStorage || tiles.floor };
    mapping[ZONE.KITCHEN] = { center: tiles.floorKitchen || tiles.floor };
    mapping[ZONE.LAUNDRY] = { center: tiles.floorLaundry || tiles.floor };
    mapping[ZONE.DOOR] = { center: tiles.door || tiles.floor };
    mapping[ZONE.DOORFRAME] = { center: tiles.doorframe || tiles.floor };
    return mapping;
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.EMPTY]: 0,
      [ZONE.GRASS]: tiles.grass || 1,
      [ZONE.ROAD]: tiles.road || tiles.asphalt || 1,
      [ZONE.SIDEWALK]: tiles.sidewalk || 1,
      [ZONE.ASPHALT]: tiles.asphalt || 1,
      [ZONE.WALL]: tiles.wall,
      [ZONE.FLOOR]: tiles.floor,
      [ZONE.STORE]: tiles.floorStore || tiles.floor,
      [ZONE.STORAGE]: tiles.floorStorage || tiles.floor,
      [ZONE.KITCHEN]: tiles.floorKitchen || tiles.floor,
      [ZONE.LAUNDRY]: tiles.floorLaundry || tiles.floor,
      [ZONE.DOOR]: tiles.door || tiles.floor,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  _resolveObjectZones(grid, tiles) {
    const mapping = {
      [ZONE.EMPTY]: 0,
      [ZONE.WALL_EXT]: tiles.wallExt || tiles.wall,
      [ZONE.WALL_INT]: tiles.wallInt || tiles.wall,
      [ZONE.WINDOW]: tiles.window || 0,
      [ZONE.DOOR]: tiles.door || tiles.floor,
      [ZONE.DOORFRAME]: tiles.doorframe || tiles.floor,
      [ZONE.FENCE]: tiles.fence || 0,
      [ZONE.TREE]: tiles.tree || 0,
      [ZONE.BUSH]: tiles.bush || 0,
      [ZONE.CRATE]: tiles.crate || 0,
      [ZONE.BARREL]: tiles.barrel || 0,
      [ZONE.COUNTER]: tiles.counter || 0,
      [ZONE.TABLE]: tiles.table || 0,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  static DEFAULT_TILES = {
    wall: 5,
    wallExt: 5,
    wallInt: 5,
    floor: 6,
    door: 7,
    doorframe: 6,
    grass: 1,
    road: 16,
    asphalt: 16,
    sidewalk: 8,
    window: 5,
    fence: 23,
    tree: 10,
    bush: 11,
    crate: 41,
    barrel: 40,
    counter: 42,
    table: 38,
    floorStore: 6,
    floorStorage: 6,
    floorKitchen: 6,
    floorLaundry: 6,
  };

  static DEFAULT_OPTIONS = {
    stores: 3,
    quality: 'draft',
  };

  static getEmittedZones() {
    return [
      ZONE.GRASS, ZONE.ROAD, ZONE.SIDEWALK, ZONE.ASPHALT, ZONE.WALL_EXT, ZONE.WALL_INT, ZONE.WALL, ZONE.DOOR, ZONE.DOORFRAME, ZONE.WINDOW, ZONE.FENCE, ZONE.FLOOR, ZONE.STORE, ZONE.STORAGE, ZONE.KITCHEN, ZONE.LAUNDRY, ZONE.TREE, ZONE.BUSH, ZONE.CRATE, ZONE.BARREL, ZONE.COUNTER, ZONE.TABLE,
    ];
  }
}
